import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { scrollY } = useScroll();
  const height = useTransform(scrollY, [0, 100], [80, 64]);
  const background = useTransform(
    scrollY,
    [0, 100],
    ['rgba(0, 0, 0, 1)', 'rgba(0, 0, 0, 1)']
  );
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About Us', href: '/about' },
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleContact = () => {
    navigate('/about');
    setIsOpen(false);
    setTimeout(() => {
      const contactSection = document.querySelector('#contact-form');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 w-full z-[99999] bg-black"
      style={{ background }}
    >
      <motion.nav
        style={{ height }}
        className="relative flex items-center justify-between container mx-auto px-4"
      >
        {/* Logo */}
        <div className="relative z-[99999] flex-shrink-0">
          <Link to="/" className="block py-4" onClick={() => setIsOpen(false)}>
            <div className="w-[160px] md:w-[240px] h-8 md:h-10 text-white">
              <svg 
                className="w-full h-full" 
                viewBox="0 0 786.37 93.72"
                fill="currentColor"
              >
                <defs>
                  <linearGradient id="logo-gradient" x1="651.27" y1="4.53" x2="743.32" y2="96.58" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#1cb899"/>
                    <stop offset=".5" stopColor="#134aa0"/>
                    <stop offset=".56" stopColor="#26459c"/>
                    <stop offset=".69" stopColor="#593992"/>
                    <stop offset=".86" stopColor="#aa2782"/>
                    <stop offset="1" stopColor="#ed1876"/>
                  </linearGradient>
                </defs>
                <path d="M12.64,80.99c-5.17-1.39-9.39-3.18-12.64-5.37l7.76-14.73c3.51,2.26,7.26,3.96,11.24,5.12,3.98,1.16,7.96,1.74,11.94,1.74s6.71-.55,8.8-1.64c2.09-1.09,3.13-2.6,3.13-4.53,0-1.66-.95-2.95-2.83-3.88-1.89-.93-4.83-1.92-8.81-2.98-4.51-1.19-8.26-2.4-11.24-3.63-2.98-1.23-5.56-3.1-7.71-5.62-2.16-2.52-3.23-5.84-3.23-9.95,0-4.97,1.36-9.33,4.08-13.08,2.72-3.75,6.6-6.65,11.64-8.71,5.04-2.05,10.95-3.08,17.71-3.08,4.97,0,9.62.51,13.93,1.54,4.31,1.03,8.03,2.5,11.14,4.43l-7.16,14.62c-2.72-1.72-5.72-3.03-9-3.93s-6.65-1.34-10.1-1.34c-3.91,0-6.98.63-9.2,1.89-2.22,1.26-3.33,2.89-3.33,4.88,0,1.73.96,3.05,2.88,3.98,1.92.93,4.91,1.92,8.96,2.98,4.51,1.13,8.24,2.29,11.19,3.48s5.49,3.02,7.61,5.47c2.12,2.46,3.19,5.67,3.19,9.65,0,4.91-1.38,9.24-4.13,12.98-2.75,3.75-6.67,6.65-11.74,8.71-5.07,2.06-10.96,3.08-17.66,3.08-5.77,0-11.24-.7-16.42-2.09Z"/>
                <path d="M86.71,79.2c-5.21-2.59-9.22-6.2-12.04-10.84-2.82-4.64-4.23-9.95-4.23-15.92,0-7.96,1.84-15.12,5.52-21.49,3.68-6.37,8.82-11.34,15.42-14.92,6.6-3.58,14.11-5.37,22.54-5.37,7.03,0,13.15,1.29,18.36,3.88,5.21,2.59,9.22,6.2,12.04,10.84,2.82,4.64,4.23,9.95,4.23,15.92,0,7.96-1.84,15.12-5.52,21.49-3.68,6.37-8.82,11.34-15.42,14.92-6.6,3.58-14.11,5.37-22.54,5.37-7.03,0-13.15-1.29-18.36-3.88ZM118.3,63.48c3.32-2.19,5.87-5.12,7.66-8.81s2.69-7.74,2.69-12.19c0-4.71-1.44-8.47-4.33-11.29-2.89-2.82-6.92-4.23-12.09-4.23-4.38,0-8.22,1.09-11.54,3.28-3.32,2.19-5.87,5.12-7.66,8.81-1.79,3.68-2.69,7.74-2.69,12.19,0,4.71,1.44,8.47,4.33,11.29,2.89,2.82,6.92,4.23,12.09,4.23,4.38,0,8.22-1.09,11.54-3.28Z"/>
                <path d="M165.56,12.04h19.7l-10.75,54.02h33.23l-3.18,15.62h-52.93l13.93-69.64Z"/>
                <path d="M229.93,80.04c-4.38-2.02-7.73-4.92-10.05-8.71-2.32-3.78-3.48-8.22-3.48-13.33,0-2.12.26-4.58.8-7.36l7.76-38.6h19.7l-7.66,38.3c-.4,1.99-.6,3.81-.6,5.47,0,7.3,3.85,10.94,11.54,10.94,4.18,0,7.48-1.28,9.9-3.83,2.42-2.55,4.23-6.75,5.42-12.59l7.66-38.3h19.4l-7.86,39.5c-4.25,21.03-16.55,31.54-36.91,31.54-6.04,0-11.24-1.01-15.62-3.03Z"/>
                <path d="M314.3,27.66h-21.39l3.18-15.62h62.38l-3.18,15.62h-21.29l-10.84,54.02h-19.7l10.84-54.02Z"/>
                <path d="M365.83,12.04h19.7l-13.93,69.64h-19.7l13.93-69.64Z"/>
                <path d="M404.88,79.2c-5.21-2.59-9.22-6.2-12.04-10.84-2.82-4.64-4.23-9.95-4.23-15.92,0-7.96,1.84-15.12,5.52-21.49,3.68-6.37,8.82-11.34,15.42-14.92,6.6-3.58,14.11-5.37,22.54-5.37,7.03,0,13.15,1.29,18.36,3.88,5.21,2.59,9.22,6.2,12.04,10.84,2.82,4.64,4.23,9.95,4.23,15.92,0,7.96-1.84,15.12-5.52,21.49-3.68,6.37-8.82,11.34-15.42,14.92-6.6,3.58-14.11,5.37-22.54,5.37-7.03,0-13.15-1.29-18.36-3.88ZM436.47,63.48c3.32-2.19,5.87-5.12,7.66-8.81s2.69-7.74,2.69-12.19c0-4.71-1.44-8.47-4.33-11.29-2.89-2.82-6.92-4.23-12.09-4.23-4.38,0-8.22,1.09-11.54,3.28-3.32,2.19-5.87,5.12-7.66,8.81-1.79,3.68-2.69,7.74-2.69,12.19,0,4.71,1.44,8.47,4.33,11.29,2.89,2.82,6.92,4.23,12.09,4.23,4.38,0,8.22-1.09,11.54-3.28Z"/>
                <path d="M549.9,12.04l-13.93,69.64h-16.22l-22.98-38.21-7.66,38.21h-19.3l13.93-69.64h16.22l23.08,38.11,7.56-38.11h19.3Z"/>
                <path d="M559.55,80.99c-5.17-1.39-9.39-3.18-12.64-5.37l7.76-14.73c3.52,2.26,7.26,3.96,11.24,5.12,3.98,1.16,7.96,1.74,11.94,1.74s6.71-.55,8.8-1.64c2.09-1.09,3.13-2.6,3.13-4.53,0-1.66-.95-2.95-2.83-3.88-1.89-.93-4.83-1.92-8.81-2.98-4.51-1.19-8.26-2.4-11.24-3.63-2.99-1.23-5.56-3.1-7.71-5.62-2.16-2.52-3.23-5.84-3.23-9.95,0-4.97,1.36-9.33,4.08-13.08,2.72-3.75,6.6-6.65,11.64-8.71,5.04-2.05,10.94-3.08,17.71-3.08,4.97,0,9.62.51,13.93,1.54,4.31,1.03,8.02,2.5,11.14,4.43l-7.16,14.62c-2.72-1.72-5.72-3.03-9-3.93s-6.65-1.34-10.1-1.34c-3.91,0-6.98.63-9.2,1.89-2.22,1.26-3.33,2.89-3.33,4.88,0,1.73.96,3.05,2.89,3.98,1.92.93,4.91,1.92,8.95,2.98,4.51,1.13,8.24,2.29,11.19,3.48s5.49,3.02,7.61,5.47c2.12,2.46,3.18,5.67,3.18,9.65,0,4.91-1.38,9.24-4.13,12.98-2.75,3.75-6.67,6.65-11.74,8.71-5.07,2.06-10.96,3.08-17.66,3.08-5.77,0-11.24-.7-16.42-2.09Z"/>
                <path fill="url(#logo-gradient)" d="M680.81,66.76h-25.85l14.62-11.35c6.04-4.57,10.26-8.75,12.69-12.53,2.42-3.78,3.63-7.86,3.63-12.24,0-3.65-1.1-6.95-3.29-9.9-2.19-2.95-5.22-5.26-9.1-6.91-3.88-1.66-8.31-2.49-13.28-2.49-6.3,0-12.02,1.28-17.16,3.83-5.15,2.55-9.11,5.98-11.89,10.3l13.13,8.65c1.72-2.25,3.7-3.98,5.92-5.17,2.22-1.2,4.56-1.79,7.01-1.79,2.72,0,4.81.58,6.27,1.74,1.46,1.16,2.19,2.73,2.19,4.72s-.66,3.93-1.99,5.82c-1.33,1.9-3.75,4.2-7.26,6.92l-30.45,23.68-2.48,12.34h48.79l8.5-15.62ZM755.16,28.8l-6.31,12.13,18.92,7.22-27.39,9.05-8.97,17.26,51.88-17.85,3.08-15.32-31.21-12.49ZM752.35,0l-48.75,93.72h-19.7l42.75-78.5h-8.52l6-15.22h28.22ZM680.81,66.76l-8.5,15.62"/>
              </svg>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex items-center space-x-8">
          {navItems.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                className={cn(
                  "relative py-2 font-medium transition-colors duration-300",
                  location.pathname === item.href ? "text-white" : "text-gray-300",
                  "hover:text-white",
                  "after:absolute after:left-0 after:bottom-0 after:h-0.5",
                  "after:w-0 after:bg-accent after:transition-all after:duration-300",
                  "hover:after:w-full"
                )}
              >
                {item.name}
              </Link>
            </li>
          ))}
          <motion.button
            onClick={handleContact}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-5 py-2 border-2 border-accent text-accent rounded-lg font-medium 
                     hover:bg-accent/10 transition-colors duration-300"
          >
            Contact Us
          </motion.button>
        </ul>

        {/* Mobile Menu Button */}
        <button
          className="relative z-[99999] flex md:hidden items-center justify-center w-12 h-12 text-white hover:bg-white/10 rounded-lg transition-colors duration-300"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Mobile Menu */}
        <div
          className={cn(
            "fixed inset-0 bg-black md:hidden transition-opacity duration-300",
            isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          style={{ zIndex: 99998 }}
        >
          <div
            className={cn(
              "fixed top-0 right-0 bottom-0 w-[280px] bg-black transition-transform duration-300 ease-out",
              isOpen ? "translate-x-0" : "translate-x-full"
            )}
            style={{ zIndex: 99999 }}
          >
            <div className="flex flex-col h-full pt-20 px-6">
              <ul className="space-y-4">
                {navItems.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        "block py-2 text-lg transition-colors duration-300",
                        location.pathname === item.href ? "text-white" : "text-gray-300",
                        "hover:text-white"
                      )}
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <button
                  onClick={handleContact}
                  className="w-full py-3 border-2 border-accent text-accent rounded-lg font-medium
                         hover:bg-accent/10 transition-colors duration-300"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>
    </motion.header>
  );
};

export default Navigation;